import Minilog from 'minilog';
import {addErrorMessage, addSuccessMessage, disablePageLoad, dismissAllMessages, enablePageLoad} from './feedback';
import {createMergeAction, SiteSettings, PrivilegedSiteComponent} from './base';
import {V3Resource, HttpStatusCodes} from './services';
import {TEXT_ERROR_LOGOUT, loadLanguage, TEXT_SUCCESS} from './locale';
import {ACCOUNT_ROUTE_LOGIN} from './routes/main';
import {setWindowLocation} from './core/browser';

const TEXT_CANNOT_LOAD_LANGUAGE = 'language_cannot_load';
const TEXT_CHANGING_LANG = 'language_changing';

let logger = Minilog('frontend.models.account');

export enum PlatformRoles {
    ViewCompanies = 'view-company',
    ViewCompanyUsers = 'view-company-company',
    ManageCompanies = 'manage-company',
    ManageCompanyUsers = 'manage-company-user',
    PartnerAdmin = 'partner-admin',
}

/**
 * Format of company data stored in global state
 */
export interface CompanyData {
    id: string;
    name: string;
}

/**
 * Format of user data stored in global state
 */
export interface UserData {
    id: number;
    oid: string;
    username: string;
    email: string;
    mobile: string;
    notification_email: string;
    is_superuser: boolean;
    has_admin_access: boolean;
    date_joined: string;
    language: string;
    company: CompanyData;
    timezone: string;
    broker: {
        username: string;
        password: string;
        vhost: string;
    };
    features: {
        hide_powered_by: boolean;
        hide_report_tab: boolean;
        site_styling: boolean;
        email_show_zeto_address: boolean;
        show_language_bar: boolean;
    };
    notification_mute_timeframes: any;
    temperature_uom: string;
    is_partner_admin: boolean;
    realm: string;
    roles: Array<PlatformRoles>;
}

/**
 * Resource for loading and editing user information
 */
class UserResource extends V3Resource {
    constructor() {
        super(V3Resource.USER);
    }
}

/**
 * Resource for loading and editing user session
 */
class SessionResource extends V3Resource {
    constructor() {
        super(V3Resource.SESSION);
    }
}

/**
 * Global user API instance
 *
 * @type {UserResource}
 */
export let UserAPI: UserResource = new UserResource();

/**
 * Global session API instance
 *
 * @type {SessionResource}
 */
export let SessionAPI: SessionResource = new SessionResource();

/**
 * Merge: set data for Premises page
 */
function setUserInfo(userInfo: any) {
    return createMergeAction(
        'userinfo_set',
        {data: {user: userInfo}}
    );
}

function redirectToPage(redirectTo: string) {
    let redirectUrl = new URL(window.location.protocol + redirectTo);
    let curLocation = window.location.href;
    let params = new URLSearchParams(redirectUrl.search);
    // T&Cs middleware uses `returnTo`, Django auth uses `next`
    if (params.get('returnTo')) {
        params.set('returnTo', curLocation);
    } else {
        params.set('next', curLocation);
    }
    redirectUrl.search = params.toString();
    setWindowLocation(redirectUrl.toString());
}

/**
 * Does the given user data has the privileges to access the given
 * site component?
 *
 * @param user
 * @param component
 * @return true if the user has access; false otherwise
 */
export function hasComponentAccess(user: UserData, component: PrivilegedSiteComponent) : boolean {
    let allowed;
    let isStaff = user.is_superuser || user.has_admin_access;
    switch (component) {
        case PrivilegedSiteComponent.Calibration:
        case PrivilegedSiteComponent.Admin:
            allowed = isStaff;
            break;
        case PrivilegedSiteComponent.CabinetPermissions:
            allowed = isStaff ||
                user.roles.indexOf(PlatformRoles.PartnerAdmin) > -1 ||
                user.roles.indexOf(PlatformRoles.ManageCompanyUsers) > -1;
            break;
        case PrivilegedSiteComponent.UserManagement:
            allowed = isStaff ||
                user.roles.indexOf(PlatformRoles.PartnerAdmin) > -1 ||
                user.roles.indexOf(PlatformRoles.ManageCompanyUsers) > -1 ||
                user.roles.indexOf(PlatformRoles.ViewCompanyUsers) > -1 ||
                user.roles.indexOf(PlatformRoles.ManageCompanies) > -1 ||
                user.roles.indexOf(PlatformRoles.ViewCompanies) > -1;
            break;
        default:
            allowed = false;
            break;
    }
    return allowed;
}

/**
 * Thunk action that retrieves the user data for the current logged-in user
 * and stores it in global state.
 * Checks authenticated state for user, redirecting to appropriate page
 * if user has become unauthenticated.
 */
export function getUserData() : Function {
    return function (dispatch) {
        return UserAPI.get<any>().then(function(response) {
            dispatch(setUserInfo(response.data));
        }).catch(function(error) {
            logger.error('Failed to retrieve user data!', error);
            // error could be redirect converted to more malleable response
            let redirectLocation = error.data ? error.data.Location : '';
            if (redirectLocation) {
                redirectToPage(SiteSettings.uri.main + redirectLocation);
            } else {
                redirectToPage(ACCOUNT_ROUTE_LOGIN);
            }
        });
    };
}

/**
 * Thunk action that retrieves the user data for the current logged-in user
 * and stores it in global state.
 */
export function logoutUser() : Function {
    return function (dispatch) {
        return SessionAPI.get<any>().then(function(response) {
            return SessionAPI.delete(response.data.key);
        }).then(function() {
            redirectToPage(ACCOUNT_ROUTE_LOGIN);
        }).catch(function() {
            dispatch(addErrorMessage(TEXT_ERROR_LOGOUT));
        });
    };
}

export function updateUserLanguage(langCode: string ) {
    return dispatch => {
        dispatch(enablePageLoad(TEXT_CHANGING_LANG));
        return updateUser(dispatch, {'language': langCode})
            .then(response => {
                dispatch(loadLanguage(response.data.language));
                dispatch(enablePageLoad(TEXT_CHANGING_LANG));
            })
            .catch((reason) => {
                dispatch(addErrorMessage(TEXT_CANNOT_LOAD_LANGUAGE));
                dispatch(disablePageLoad());
            });
    };
}

export function updateUserSettings(data: any) {
    return dispatch => {
        dispatch(enablePageLoad(TEXT_CHANGING_LANG));
        return updateUser(dispatch, data)
            .then(response => {
                dispatch(loadLanguage(response.data.language));
                dispatch(enablePageLoad(TEXT_CHANGING_LANG));
            })
            .catch((reason) => {
                dispatch(disablePageLoad());
            });
    };
}

export function updateUserPassword(data: any, textForKey) {
    let d = {'passwords': data};
    return dispatch => {
        return updateUser(dispatch, d)
            .then(response => {
                dispatch(dismissAllMessages());
                // update password toast 'success'
                dispatch(addSuccessMessage(TEXT_SUCCESS));

            })
            .catch((reason) => {
                dispatch(dismissAllMessages());
                // update password toast 'failure'
                dispatch(addErrorMessage(reason.data.password));
            });
    };
}

export function updateUser(dispatch, data: any) : Promise<any> {
    return UserAPI.update('', data).then(function(response) {
        dispatch(setUserInfo(response.data));
        return Promise.resolve(response);
    });

}

export function setContactModalValidationErrors(validationErrors: {[input: string]: string[]}) {
    return createMergeAction(
        'contact_modal_errors',
        {ui: {accounts: {contactModal: {validationErrors}}}},
        {array_replace: true}
    );
}

/**
 * Does the given user data have valid subscribe details?
 *
 * @param userData
 */
export function hasSubscribeDetails(userData: UserData) {
    return (userData.email || userData.mobile);
}
